import React from 'react';
import { CallUpsertContainerProps, CallUpsertViewProps } from './callUpsertProps';
import { IntegrationContainerBaseConfig } from '../../../base';
import { CallUpsertController, CallUpsertLoader } from './callUpsertInterface';
import { assert } from '@varos/util-typescript';
import { createCallPreset } from '@varos/call-preset';
import { createAssistantSdk } from '@varos/assistant-sdk';
import { Call } from '@varos/call-core';
import { EnvironmentVariables } from '../../../../../../../config/environment';

export function createCallUpsertContainer(
    config: IntegrationContainerBaseConfig,
    loader: CallUpsertLoader,
    controller: CallUpsertController,
    View: React.FC<CallUpsertViewProps>
): React.FC<CallUpsertContainerProps> {
    const {
        context: { useContext },
        kernel: {
            infra: {
                toaster: { useToast },
            },
        },
    } = config;

    // Enable STUN test mode for development/testing
    const STUN_TEST_MODE = false;
    const DEBUG_ICE_SERVERS = false; // Set to true to see detailed ICE logs

    const {
        call: { useCall },
    } = createCallPreset({
        client: createAssistantSdk({
            baseUrl: EnvironmentVariables.ASSISTANT_API_BASE_URL,
        }),
        autoStart: true,
        stunTestMode: STUN_TEST_MODE,
        debugIceServers: DEBUG_ICE_SERVERS,
    });

    return ({ ...containerProps }) => {
        const context = useContext();
        const toast = useToast();

        const data = loader.useLoad(context, {
            integration: containerProps.integration,
            tool: containerProps.tool,
            thread: containerProps.thread,
        });

        // Use the call strategy with ApplicationContext (cast through unknown)
        const call = useCall(context, data.call, {
            options: {
                username: data.turnCredentials.username,
                password: data.turnCredentials.password,
                uris: data.turnCredentials.uris,
                ttl: data.turnCredentials.ttl,
            },
            async onConnect() {
                console.log('Call connected');
            },
            async onEnd(call) {
                console.log('on end from upsert container', call);
                await containerProps.onEnd(call);
            },
            async onError(error: Error) {
                console.error(error);
                toast({
                    kind: 'error',
                    description: 'Call connection failed',
                });
            },
            onUserSpeaking() {
                console.log('User is speaking');
            },
            onSystemSpeaking() {
                console.log('System is speaking');
            },
        });

        const props = controller.useProps(context, data, {
            integration: containerProps.integration,
            call: call,
            mute: {
                value: call.muted,
                onChange(value) {
                    if (value) {
                        call.mute();
                    } else {
                        call.unmute();
                    }
                },
            },
            onInterrupt() {
                call.interrupt();
            },
        });

        return <View {...props} />;
    };
}
